import { AxiosResponse } from "axios";
import { TestRequest } from "../models/test-request/TestRequest";
import Attachment from "../../components/Gallery/model/Attachment.model";
import Api from "./Api";
import { TestRequestComment, TestRequestSearchResult } from "../models/TestRequest";
import { TestCaseSearchResult } from "../models/test-series/TestCaseSearchResult";
import { UUID } from "../models/Uuid";
import downloadFile from "../../functions/downloadFile.function";
import ApiResponse from "../models/ApiResponse";
import UpdateIndividualTestResultRQ from "../models/test-result/UpdateIndividualTestResultRQ";
import IndividualTestResultDTO from "../models/test-result/IndividualTestResultDTO";
import TestRequestResponse from "../models/test-request/TestRequestResponse";
import {
  CompetitorProductSample,
  TesaProductSample,
  TesaProductSampleRequest,
  TesaPrototypeSample,
  UnknownProductSample,
} from "../models/product/ProductSampleReference";
import CreateCompetitorProductSampleRQ from "../models/product/CreateCompetitorProductSampleRQ";
import TestPlanResponse from "../models/test-series/TestPlanResponse";
import TestSeriesDTO from "../models/TestSeriesDTO";
import ProjectConfidentiality from "../models/test-request/ProjectConfidentiality";
import PagedResponse from "../models/PagedResponse";
import WaitingFilter from "../models/WaitingFilter";
import PrioritySearchTerm from "../models/test-request/PrioritySearchTerm";

type TestRequestStateCount = Required<TestRequestRaw.Schemas.TestRequestStateCountResponse>;
type LinkedTestSeriesCreateConfigRq = TestRequestRaw.Schemas.LinkedTestSeriesCreateConfigRq;
type TestPlanVersionIdentifier = TestRequestRaw.Schemas.TestPlanVersionIdentifier;
type UserInformation = TestRequestRaw.Schemas.UserInformation;
type CSCLaboratory = TestRequestRaw.Schemas.CSCLaboratory;
type TestRequestDescriptionEstimatedTestEffortRequest =
  TestRequestRaw.Schemas.TestRequestDescriptionEstimatedTestEffortRequest;

export interface TestRequestCountResponse {
  counts: TestRequestStateCount[];
  total: number;
}

class TestRequestApi {
  constructor(private api: Api) {}

  public getUser(): Promise<AxiosResponse<UserInformation>> {
    return this.api.get(`/test-request/me`);
  }

  public getTestRequest(requestId: string): Promise<AxiosResponse<TestRequestResponse>> {
    return this.api.get(`/test-request/v2/testrequests/${requestId}`);
  }

  public duplicateTestRequest(requestId: string): Promise<AxiosResponse> {
    return this.api.put(`/test-request/v2/testrequests/${requestId}/duplicate`);
  }

  public reuseSamplesAndTestPlans(requestId: string): Promise<AxiosResponse<TestRequestResponse>> {
    return this.api.put(`/test-request/v2/testrequests/${requestId}/reuse-samples-and-test-plans`);
  }

  public copySamplesAndTestPlans(requestId: string): Promise<AxiosResponse<TestRequestResponse>> {
    return this.api.put(`/test-request/v2/testrequests/${requestId}/copy-samples-and-test-plans`);
  }

  public updateEstimatedTestEffort(
    requestId: string,
    payload: TestRequestDescriptionEstimatedTestEffortRequest,
  ): Promise<AxiosResponse<TestRequestResponse>> {
    return this.api.put(`/test-request/v2/testrequests/${requestId}/estimatedtesteffort`, payload);
  }

  public assignTestRequestToUser(
    requestId: string,
    cscLaboratory: CSCLaboratory | false,
    userId: string,
  ): ApiResponse<TestRequestResponse> {
    const query = new URLSearchParams();
    if (cscLaboratory) {
      query.append("cscLaboratory", cscLaboratory);
    }
    return this.api.put(`/test-request/v2/testrequests/${requestId}/assignToUser/${userId}?${query}`);
  }

  public searchTestRequests(
    pageIndex: number,
    pageSize: number,
    sortColumn: string,
    sortDirection: string,
    searchTerm: string,
  ): ApiResponse<PagedResponse<TestRequestSearchResult>> {
    return this.api.get(
      `/test-request/search?pageIndex=${pageIndex || 0}&pageSize=${pageSize}&sortColumn=${
        sortColumn || "CREATION_DATE"
      }&sortDirection=${sortDirection.toUpperCase()}&searchTerm=${searchTerm}`,
    );
  }

  public listTestRequests(
    pageIndex: number,
    pageSize: number,
    requestorEmail: string,
    labKey: string,
    sortColumn: string,
    sortDirection: string,
    terms: string[],
    prioritySearchTerm: PrioritySearchTerm,
    statesQueryString: string,
    waitingFilter?: WaitingFilter,
  ): ApiResponse<PagedResponse<TestRequestSearchResult>> {
    const requestorEmailPart = requestorEmail ? `&requestorEmail=${requestorEmail}` : "";
    const labKeyPart = labKey ? `&labs=${labKey}` : "";
    const termsQuery = terms.map((term) => `terms=${encodeURIComponent(term)}`).join("&");
    const termsQueryPart = termsQuery ? `&${termsQuery}` : "";
    const priorityQueryPart = prioritySearchTerm ? `&priorityTerm=${encodeURIComponent(prioritySearchTerm)}` : "";
    const waitingFilterPart = waitingFilter ? `&waitingFilter=${waitingFilter}` : "";
    return this.api.get(
      `/test-request/v2/testrequests?pageIndex=${
        pageIndex || 0
      }&pageSize=${pageSize}${requestorEmailPart}${labKeyPart}&sortColumn=${
        sortColumn || "CREATION_DATE"
      }&sortDirection=${sortDirection.toUpperCase()}${termsQueryPart}${priorityQueryPart}${statesQueryString}${waitingFilterPart}`,
    );
  }

  public countTestRequests(
    requestorEmail: string | undefined,
    labKey: string | undefined,
    terms: string[],
    waitingFilter?: WaitingFilter,
  ): Promise<AxiosResponse<TestRequestCountResponse>> {
    const queryParams = new URLSearchParams();

    if (requestorEmail) queryParams.append("requestorEmail", requestorEmail);
    if (labKey) queryParams.append("labs", labKey);
    if (waitingFilter) queryParams.append("waitingFilter", waitingFilter);
    terms.forEach((term) => queryParams.append("terms", term));

    const queryString = queryParams.toString();
    return this.api.get(`/test-request/v2/testrequests/count${queryString ? `?${queryString}` : ""}`);
  }

  public getTestRequestAttachmentClearanceToken(requestId: string): Promise<AxiosResponse<string>> {
    return this.api.get(`/test-request/v2/testrequests/${requestId}/attachmentsClearanceToken`);
  }

  public getDownloadAllTestPlanAttachmentsClearanceToken(testRequestId: string): Promise<AxiosResponse<string>> {
    return this.api.get(`/test-request/v2/testrequests/${testRequestId}/downloadAllTestPlansAttachmentsClearanceToken`);
  }

  public deleteTestRequestSubstrateSamples(requestId: string | null, payload: Array<number>): Promise<AxiosResponse> {
    return this.api.deleteByPayload(`/test-request/v2/testrequests/${requestId}/substrates`, payload);
  }

  public addTestRequestSubstrateSample(
    requestId: string,
    sampleId: number,
  ): Promise<AxiosResponse<TestRequestRaw.Schemas.TestRequestResponse>> {
    return this.api.post(`/test-request/v2/testrequests/${requestId}/substrates`, sampleId, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  public cloneThenAddSubstrateSampleToTestRequest(
    requestId: string,
    substrateSampleId: number,
  ): Promise<AxiosResponse<TestRequestRaw.Schemas.TestRequestResponse>> {
    return this.api.post(
      `/test-request/v2/testrequests/${requestId}/substrates/addCloned`,
      JSON.stringify(substrateSampleId),
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
  }

  public updateTestRequestPriority(
    requestId: string,
    newPriority: string,
  ): Promise<AxiosResponse<TestRequestRaw.Schemas.TestRequestResponse>> {
    return this.api.patch(`/test-request/v2/testrequests/${requestId}/priority`, newPriority, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  public createCompetitorProductSample(
    payload: CreateCompetitorProductSampleRQ,
  ): Promise<AxiosResponse<CompetitorProductSample>> {
    return this.api.post("/test-request/product-samples/competitor-product", payload);
  }

  public createUnknownProduct(payload: UnknownProductSample & { sampleName: string }): Promise<AxiosResponse> {
    return this.api.post("/test-request/product-samples/unknown-product", payload);
  }

  public createTesaProduct(payload: TesaProductSampleRequest): ApiResponse<TesaProductSample> {
    return this.api.post("/test-request/product-samples/tesa-product", payload);
  }

  public createPrototypeSample(payload: TesaPrototypeSample): Promise<AxiosResponse> {
    return this.api.post(`/test-request/product-samples/tesa-prototype`, payload);
  }

  public generateTestCasesById(id: number): Promise<AxiosResponse> {
    return this.api.put(`/test-request/test-cases/test-series/${id}/generate`, {});
  }

  public updateTestCasesExclusionById(testSeriesid: number, body: unknown): Promise<AxiosResponse> {
    return this.api.put(`/test-request/test-cases/test-series/${testSeriesid}`, body);
  }

  public getTestCasesById(id: number): Promise<AxiosResponse<TestCaseSearchResult[]>> {
    return this.api.get(`/test-request/test-series/${id}/testcases`, {});
  }

  public getTestCasesByIdAndVersion(
    testSeriesId: number,
    testSeriesVersion: number,
  ): Promise<AxiosResponse<TestCaseSearchResult[]>> {
    return this.api.get(`/test-request/test-series/${testSeriesId}/version/${testSeriesVersion}/testcases`, {});
  }

  public updateTestSeriesToExecute(id: number): ApiResponse<TestSeriesDTO> {
    return this.api.put(`/test-request/test-series/${id}/execute`, {});
  }

  public checkIfTestSeriesIsCompletable(id: number): Promise<AxiosResponse<void>> {
    return this.api.get(`/test-request/test-series/${id}/check-completable`);
  }

  public updateTestSeriesToComplete(id: number, ignoreUnratedTestCases: boolean): Promise<AxiosResponse> {
    return this.api.put(
      `/test-request/test-series/${id}/complete?ignoreUnratedTestCases=${ignoreUnratedTestCases}`,
      {},
    );
  }

  public updateTestSeries(payload: unknown): Promise<AxiosResponse<TestSeriesDTO>> {
    return this.api.put(`/test-request/test-series/`, payload);
  }

  public getTestResultsByTestCaseId(id: number): Promise<AxiosResponse<IndividualTestResultDTO>> {
    return this.api.get(`/test-request/test-results/${id}`);
  }

  public updateTestResults(payload: UpdateIndividualTestResultRQ): Promise<AxiosResponse> {
    return this.api.put("/test-request/test-results/", payload);
  }

  public addRepetition(testCaseId: number): Promise<AxiosResponse<IndividualTestResultDTO>> {
    return this.api.get(`/test-request/test-results/${testCaseId}/add-repetition`);
  }

  public getTestSeriesByTestMethod(
    testMethod: { id: number; version: number; template: boolean },
    testPlanId: number,
  ): ApiResponse<TestSeriesDTO> {
    const payload = {
      testMethodId: testMethod.id,
      testMethodVersion: testMethod.version,
      template: testMethod.template,
    };
    return this.api.post(`/test-request/test-plans/${testPlanId}/test-series`, payload);
  }

  public createRegainedTestSeries(
    testPlanId: number,
    testSeriesId: number,
    linkedTestSeriesCreateConfig: LinkedTestSeriesCreateConfigRq,
  ): ApiResponse<TestSeriesDTO> {
    return this.api.post(
      `/test-request/test-plans/${testPlanId}/test-series/${testSeriesId}`,
      linkedTestSeriesCreateConfig,
    );
  }

  public addTestPlanToTestRequest(
    testRequestId: string,
    testSpecId: number,
    testSpecVersion: number,
  ): Promise<AxiosResponse<TestPlanResponse>> {
    const query = `testPlanId=${testSpecId}&testPlanVersionId=${testSpecVersion}`;
    return this.api.put(`/test-request/test-plans/predefined-test-specification/${testRequestId}?${query}`);
  }

  public addTestPlansToTestRequest(
    testRequestId: string,
    payload: TestPlanVersionIdentifier[],
  ): Promise<AxiosResponse<TestPlanResponse>> {
    return this.api.post(
      `/test-request/v2/testrequests/${testRequestId}/test-plans/predefined-test-specification`,
      payload,
    );
  }

  public addAttachmentToRequest(testRequestId: string, uuids: string[]): Promise<Attachment[]> {
    return this.api
      .put(`/test-request/v2/testrequests/${testRequestId}/test-request-attachments`, uuids)
      .then((response: AxiosResponse<TestRequest>) => response.data.descriptionAttachments);
  }

  public addSpecificationAttachmentToRequest(testRequestId: string, uuids: string[]): Promise<TestRequest> {
    return this.api
      .put(`/test-request/v2/testrequests/${testRequestId}/set-specification-needed`, uuids)
      .then((response: AxiosResponse<TestRequest>) => response.data);
  }

  public addReportAttachmentToRequest(testRequestId: string, reportAttachmentUUID: string): Promise<AxiosResponse> {
    return this.api.put(`/test-request/v2/testrequests/${testRequestId}/report-attachments`, [reportAttachmentUUID]);
  }

  public removeAttachmentFromRequest(testRequestId: string, uuids: string[]): Promise<Attachment[]> {
    return this.api
      .delete(`/test-request/v2/testrequests/${testRequestId}/test-request-attachments`, uuids)
      .then((response: AxiosResponse<TestRequest>) => response.data.descriptionAttachments);
  }

  public removeReportAttachmentFromRequest(
    testRequestId: string,
    reportAttachmentUUID: string,
  ): ApiResponse<TestRequestResponse> {
    return this.api.delete(`/test-request/v2/testrequests/${testRequestId}/report-attachments`, [reportAttachmentUUID]);
  }

  public addAttachmentToTestResultRow(
    testResultId: number,
    testResultRowId: number,
    type: "DOCUMENT" | "PICTURE",
    uuids: string[],
  ): Promise<Attachment[]> {
    return this.api
      .put(`/test-request/test-results/${testResultId}/${testResultRowId}/attachments/${type}`, uuids)
      .then((response) => response.data.attachments);
  }

  public removeAttachmentFromTestResultRow(
    testResultId: number,
    testResultRowId: number,
    uuids: string[],
  ): Promise<Attachment[]> {
    return this.api
      .delete(`/test-request/test-results/${testResultId}/${testResultRowId}/attachments`, uuids)
      .then((response) => response.data.attachments);
  }

  // pageSize = 0 will fetch all comments
  public getTestPagedRequestComments(requestId: string, pageSize?: number): Promise<TestRequestComment[]> {
    return this.api
      .get(`/test-request/test-request-comments/${requestId}?pageSize=${pageSize || 0}`)
      .then((response: AxiosResponse<TestRequestRaw.Schemas.CommentsPagedResponse>) => {
        if (!response.data?.data) {
          throw Error("Invalid response from server");
        }
        return response.data?.data as TestRequestComment[];
      });
  }

  public createTestRequestComment(
    requestId: string,
    payload: string,
  ): Promise<AxiosResponse<TestRequestRaw.Schemas.CommentResponse>> {
    return this.api.post(`/test-request/test-request-comments/${requestId}`, payload, {
      headers: { "content-type": "application/json" },
    });
  }

  public updateTestRequestComment(
    requestId: string,
    commentId: number,
    payload: string,
  ): Promise<AxiosResponse<TestRequestRaw.Schemas.CommentResponse>> {
    return this.api.put(`/test-request/test-request-comments/${requestId}/${commentId}`, payload, {
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  public deleteTestRequestComment(requestId: string, commentId: number): Promise<AxiosResponse<void>> {
    return this.api.delete(`/test-request/test-request-comments/${requestId}/${commentId}`);
  }

  public setAllSamplesReceived(
    requestId: string,
    receivedOn: string, // date part of iso string
  ): ApiResponse<TestRequestResponse> {
    return this.api.put(
      `/test-request/v2/testrequests/${requestId}/set-all-samples-received`,
      JSON.stringify(receivedOn),
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
  }

  public updateUserMapping(
    payload: FormData,
    progressCallback?: (progressEvent: { loaded: number; total: number }) => void,
  ): Promise<AxiosResponse<TestRequestRaw.Schemas.ImportResult>> {
    const file = payload.get("file");
    if (!payload.get("filename") && file && typeof file === "object" && file.name) {
      payload.append("filename", file.name.normalize());
    }
    return this.api.post("/test-request/v2/user-mapping", payload, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: progressCallback,
    });
  }

  public updateCountryList(
    payload: FormData,
    progressCallback?: (progressEvent: { loaded: number; total: number }) => void,
  ): Promise<AxiosResponse<TestRequestRaw.Schemas.ImportResult>> {
    const file = payload.get("file");
    if (!payload.get("filename") && file && typeof file === "object" && file.name) {
      payload.append("filename", file.name.normalize());
    }
    return this.api.post("/test-request/country", payload, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: progressCallback,
    });
  }

  public downloadTemplate(): Promise<void> {
    return this.api
      .get("/test-request/country/template", {
        responseType: "arraybuffer",
      })
      .then((response) => downloadFile(response, "template.xlsx"));
  }

  public validateTestRequestDescription(
    description: TestRequestRaw.Schemas.TestRequestDescriptionRq,
  ): Promise<AxiosResponse<void>> {
    return this.api.put("/test-request/v2/testrequests/validate", description);
  }

  public getClearanceToken(attachmentReference: UUID): Promise<AxiosResponse<string>> {
    return this.api.get(`/test-request/v2/attachments/${attachmentReference}/clearance-token`);
  }

  public updateConfidentiality(
    requestId: string,
    confidentiality: ProjectConfidentiality,
  ): ApiResponse<TestRequestResponse> {
    return this.api.put(`/test-request/v2/testrequests/${requestId}/confidentiality`, JSON.stringify(confidentiality), {
      headers: {
        "content-type": "application/json",
      },
    });
  }

  public updateFurtherInternalContacts(requestId: string): ApiResponse<TestRequestResponse> {
    return this.api.put(`/test-request/v2/testrequests/${requestId}/furtherinternalcontacts`);
  }
}

export default TestRequestApi;
